// Export: where an Owner's whole-books export lives, and what it says. The act itself is not built
// (Books Module Plan BM-BE2 part 3 waits on its engine leg), so the control is shown, disabled, and
// the page says so plainly. Owners only (`books:export`): a Member never reaches this page.

import { Download } from 'lucide-react';
import { Button, Card, Icon } from '@givmo/design-system';
import type { BooksHome } from './books-wire';
import { BooksPageShell, BooksStateCard } from './books-ui';

export function ExportPage({ home, mayExport }: { home: BooksHome; mayExport: boolean }) {
  if (!mayExport) {
    return (
      <BooksPageShell title="Export" testId="books-export">
        <BooksStateCard
          testId="books-export-no-access"
          title="Only an Owner of Books can export these books"
        />
      </BooksPageShell>
    );
  }
  return (
    <BooksPageShell title="Export" testId="books-export">
      <Card className="flex flex-col gap-16 px-24 py-24" testId="books-export-card">
        <div className="flex flex-wrap items-start justify-between gap-16">
          <div className="flex max-w-[620px] flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
              A complete copy of these books
            </h2>
            <p className="font-sans text-s14 tracking-givmo text-text-secondary">
              One file holding every entry, the chart of accounts and each month’s status, exactly
              as the books stand when you export them.
            </p>
          </div>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            disabled
            leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
            testId="books-export-start"
          >
            Export books
          </Button>
        </div>
        <ul className="flex flex-col gap-6 border-t border-border-subtle pt-16">
          <li className="font-sans text-s13 tracking-givmo text-text-secondary">
            Each export is recorded in your organization’s Activity, with who exported and when.
          </li>
          <li className="font-sans text-s13 tracking-givmo text-text-secondary">
            Documents attached to entries, such as receipts, aren’t part of the export.
          </li>
        </ul>
        <p
          className="font-sans text-s13 tracking-givmo text-text-tertiary"
          data-testid="books-export-not-yet"
        >
          Exporting isn’t available yet.
        </p>
      </Card>
      {home.periods.length === 0 ? (
        <BooksStateCard
          testId="books-export-empty"
          title="Nothing to export yet"
          description="These books hold no entries yet."
        />
      ) : null}
    </BooksPageShell>
  );
}
